/**
 * mem-tracer.js — registrador en memoria de snapshots periódicos para
 * detectar leaks durante sesiones largas.
 *
 * Diseño:
 *  - Cada `intervalMs` (default 60s) toma un snapshot del estado del
 *    runtime: heap reportado, tamaños de colecciones grandes, contadores
 *    incrementales, MapLibre image manager, etc.
 *  - Snapshots se acumulan en un buffer circular de tamaño
 *    `maxSamples` (default 240 → ~4 horas con interval 60s).
 *  - El overhead es minúsculo: cada snapshot pesa ~1 KB → 240 KB total.
 *
 * Uso (operador):
 *   __memTracer.dump()       -- tabla resumen + CSV en consola, copy/paste
 *   __memTracer.export()     -- {samples, summary} para serializar
 *   __memTracer.snapshot()   -- toma una manual ad-hoc
 *   __memTracer.stop()       -- detiene el interval
 *   __memTracer.getDelta()   -- diff primero vs último
 */

const RUN_ID = (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID().slice(0, 8)
  : ('r' + Math.floor(Math.random() * 0xffffff).toString(16))

const state = {
  vm: null,                  // Vue instance (MapV2.vue) para leer su estado
  timer: null,
  samples: [],
  intervalMs: 60000,
  maxSamples: 240,
  startedAt: null,
  noticeIntervals: 30 * 60 * 1000, // log cada 30 min para confirmar que sigue vivo
  lastNotice: 0,

  // Acumuladores de la ventana actual (se resetean en cada snapshot).
  windowLongTasks: 0,            // count de long tasks > 50ms en el último minuto
  windowLongTasksMs: 0,          // suma de duración de esos long tasks
  windowMaxLongTaskMs: 0,        // peor long task del minuto
  windowLongTaskAttribution: {}, // { 'script-url': totalMs } — top 3 al snapshot
  windowRafCount: 0,             // count de rAFs en el minuto
  windowRafJank: 0,              // count de rAFs > 33ms (jank)
  windowRafTotalMs: 0,           // suma de delta entre rAFs
  windowMaxRafMs: 0,             // peor delta entre rAFs
  windowEventLoopLagSamples: [], // medidas de lag del setInterval(1000)

  // Activity counters globales — se resetean cada snapshot.
  // Otros módulos los incrementan via memTracer.tick(name).
  windowCounters: {},

  // Observers persistentes
  longTaskObserver: null,
  rafLastT: 0,
  rafScheduled: false,
  eventLoopTimer: null,
  eventLoopExpectedT: 0,

  // Para calcular delta del minuto en cluster cache
  prevClusterStats: null,
}

function nowMs() {
  return Date.now()
}

function bytesToMB(b) {
  return b == null ? null : Math.round(b / 1048576 * 10) / 10
}

function safeNumber(fn, fallback) {
  try {
    var v = fn()
    return (typeof v === 'number' && !isNaN(v)) ? v : (fallback != null ? fallback : null)
  } catch (e) {
    return fallback != null ? fallback : null
  }
}

function safeKeysCount(obj) {
  if (!obj) return 0
  try { return Object.keys(obj).length } catch (e) { return 0 }
}

function safeMapSize(m) {
  return (m && typeof m.size === 'number') ? m.size : 0
}

function safeArrayLen(a) {
  return Array.isArray(a) ? a.length : 0
}

// ─── CPU proxy signals (long tasks, rAF, event loop lag, activity counters) ──

function startLongTaskObserver() {
  if (state.longTaskObserver) return
  if (typeof PerformanceObserver === 'undefined') return
  // 'longtask' captura cualquier task del main thread > 50ms.
  // attribution[].name suele ser 'script' o 'self'; .containerSrc / containerType
  // dan el origen aproximado.
  try {
    var obs = new PerformanceObserver(function(list) {
      var entries = list.getEntries()
      for (var i = 0; i < entries.length; i++) {
        var e = entries[i]
        state.windowLongTasks++
        state.windowLongTasksMs += e.duration
        if (e.duration > state.windowMaxLongTaskMs) state.windowMaxLongTaskMs = e.duration
        // Attribution heurística: src del primer attribution que tenga
        // containerSrc no vacío. Si no, 'self' (inline script o motor).
        var attr = (e.attribution && e.attribution[0]) || null
        var src = (attr && (attr.containerSrc || attr.name)) || 'self'
        // Acortar a basename del script para legibilidad
        var basename = src
        try {
          var slash = src.lastIndexOf('/')
          if (slash >= 0) basename = src.slice(slash + 1)
          var query = basename.indexOf('?')
          if (query >= 0) basename = basename.slice(0, query)
        } catch (_) {}
        state.windowLongTaskAttribution[basename] =
          (state.windowLongTaskAttribution[basename] || 0) + e.duration
      }
    })
    obs.observe({ entryTypes: ['longtask'] })
    state.longTaskObserver = obs
  } catch (e) {
    console.warn('[memTracer] longtask observer failed:', e)
  }
}

function stopLongTaskObserver() {
  if (state.longTaskObserver) {
    try { state.longTaskObserver.disconnect() } catch (_) {}
    state.longTaskObserver = null
  }
}

function startRafChain() {
  state.rafLastT = performance.now()
  state.rafScheduled = true
  function loop(t) {
    if (!state.rafScheduled) return
    var dt = t - state.rafLastT
    state.rafLastT = t
    state.windowRafCount++
    state.windowRafTotalMs += dt
    if (dt > state.windowMaxRafMs) state.windowMaxRafMs = dt
    // jank threshold: 33ms (~30fps). Por debajo de 60fps no es necesariamente
    // jank visible pero indica thread comprometido.
    if (dt > 33) state.windowRafJank++
    requestAnimationFrame(loop)
  }
  requestAnimationFrame(loop)
}

function stopRafChain() {
  state.rafScheduled = false
}

// Mide el lag del event loop midiendo desfase de un setInterval(1000).
// Si el thread está saturado, el callback corre tarde. Útil para detectar
// "CPU al 300%": el lag llega a 100-500ms en bursts.
function startEventLoopLagProbe() {
  if (state.eventLoopTimer) return
  state.eventLoopExpectedT = performance.now() + 1000
  state.eventLoopTimer = setInterval(function() {
    var now = performance.now()
    var lag = now - state.eventLoopExpectedT
    state.eventLoopExpectedT = now + 1000
    if (lag > 0) state.windowEventLoopLagSamples.push(lag)
    // Cap para no consumir memoria si hay miles de tics
    if (state.windowEventLoopLagSamples.length > 70) {
      state.windowEventLoopLagSamples.shift()
    }
  }, 1000)
}

function stopEventLoopLagProbe() {
  if (state.eventLoopTimer) {
    clearInterval(state.eventLoopTimer)
    state.eventLoopTimer = null
  }
}

function resetWindowCounters() {
  state.windowLongTasks = 0
  state.windowLongTasksMs = 0
  state.windowMaxLongTaskMs = 0
  state.windowLongTaskAttribution = {}
  state.windowRafCount = 0
  state.windowRafJank = 0
  state.windowRafTotalMs = 0
  state.windowMaxRafMs = 0
  state.windowEventLoopLagSamples = []
  state.windowCounters = {}
}

function summarizeAttribution() {
  var entries = Object.entries(state.windowLongTaskAttribution)
  if (entries.length === 0) return ''
  entries.sort(function(a, b) { return b[1] - a[1] })
  var top = entries.slice(0, 3).map(function(e) {
    return e[0] + '=' + Math.round(e[1]) + 'ms'
  }).join('|')
  return top
}

function summarizeEventLoopLag() {
  if (state.windowEventLoopLagSamples.length === 0) return { avg: 0, max: 0, p95: 0 }
  var sorted = state.windowEventLoopLagSamples.slice().sort(function(a, b) { return a - b })
  var sum = 0
  for (var i = 0; i < sorted.length; i++) sum += sorted[i]
  var avg = sum / sorted.length
  var max = sorted[sorted.length - 1]
  var p95Idx = Math.floor(sorted.length * 0.95)
  var p95 = sorted[Math.min(p95Idx, sorted.length - 1)]
  return {
    avg: Math.round(avg * 10) / 10,
    max: Math.round(max * 10) / 10,
    p95: Math.round(p95 * 10) / 10,
  }
}

function takeSnapshot() {
  var vm = state.vm
  var t = nowMs()
  var snap = {
    t: t,
    age_s: state.startedAt ? Math.round((t - state.startedAt) / 1000) : 0,
    heap_used_mb: null,
    heap_total_mb: null,
    monitors: 0,
    monitor_total_count: 0,
    monitor_total_count_drift: 0, // sanity: vm._monitors.size vs vm.monitorTotalCount
    pulsable_monitors: 0,
    position_buffer_total: 0,
    position_buffer_max: 0,
    alerts_len: 0,
    alert_id_set: 0,
    alert_id_set_drift: 0,        // sanity: alertIdSet keys vs alerts.length
    alert_counts_total: 0,
    entity_counts_total: 0,
    entity_counts_drift: 0,       // sanity: sum(entityTypeCounts) vs monitorTotalCount
    sm_features: 0,
    sm_pending_pos: 0,
    sm_pending_prop: 0,
    sm_pending_ent: 0,
    cluster_img_cache: 0,
    // Cluster cache hit/miss del minuto (delta vs último snapshot)
    cluster_hits_60s: 0,
    cluster_misses_60s: 0,
    cluster_miss_ms_60s: 0,
    cluster_hit_rate_pct: 0,   // sobre todo el tiempo desde que arrancó
    maplibre_images: 0,
    dom_markers: 0,
    anim_queue: 0,
    cad_subs: 0,
    geofence_data: 0,
    iframes: 0,
    pending_data: 0,
    selected_item: false,
    follow_active: false,
    overlay_event_features: 0,
    // CPU proxy signals — capturadas en la ventana del minuto anterior
    long_tasks_count: 0,
    long_tasks_ms: 0,
    long_tasks_max_ms: 0,
    long_tasks_top: '',
    raf_count: 0,
    raf_jank: 0,
    raf_avg_ms: 0,
    raf_max_ms: 0,
    event_loop_lag_avg: 0,
    event_loop_lag_max: 0,
    event_loop_lag_p95: 0,
    activity_top: '', // top counters del minuto: "fooN|barM|..."
  }

  // performance.memory (Chrome only)
  if (typeof performance !== 'undefined' && performance.memory) {
    snap.heap_used_mb = bytesToMB(performance.memory.usedJSHeapSize)
    snap.heap_total_mb = bytesToMB(performance.memory.totalJSHeapSize)
  }

  if (!vm) return snap

  // Vue instance fields (no reactivos cuando son privados con _, eso es OK)
  snap.monitors = safeKeysCount(vm._monitors)
  snap.monitor_total_count = safeNumber(function() { return vm.monitorTotalCount }, 0)
  snap.monitor_total_count_drift = snap.monitors - snap.monitor_total_count
  snap.pulsable_monitors = safeKeysCount(vm._pulsableMonitors)
  snap.alerts_len = safeArrayLen(vm.alerts)
  snap.alert_id_set = safeKeysCount(vm.alertIdSet)
  snap.alert_id_set_drift = snap.alert_id_set - snap.alerts_len
  snap.cad_subs = safeKeysCount(vm._geofenceData)  // placeholder; se sobreescribe abajo si está
  snap.geofence_data = safeKeysCount(vm._geofenceData)
  snap.pending_data = safeArrayLen(vm._pendingData)
  snap.selected_item = !!(vm.$store && vm.$store.state && vm.$store.state['map-v2'] && vm.$store.state['map-v2'].selectedItem)
  snap.follow_active = !!(vm.trailRenderer && vm.trailRenderer.followedMonitorId)

  // _positionBuffer es { monitorId: [coords...] }
  if (vm._positionBuffer) {
    var pbKeys = Object.keys(vm._positionBuffer)
    var total = 0, max = 0
    for (var i = 0; i < pbKeys.length; i++) {
      var arr = vm._positionBuffer[pbKeys[i]]
      var len = Array.isArray(arr) ? arr.length : 0
      total += len
      if (len > max) max = len
    }
    snap.position_buffer_total = total
    snap.position_buffer_max = max
  }

  // alertCountsState es Object reactivo
  if (vm.alertCountsState) {
    var acVals = Object.values(vm.alertCountsState)
    var sum = 0
    for (var a = 0; a < acVals.length; a++) sum += (acVals[a] || 0)
    snap.alert_counts_total = sum
  }

  // entityTypeCounts
  if (vm.entityTypeCounts) {
    var etVals = Object.values(vm.entityTypeCounts)
    var etSum = 0
    for (var e = 0; e < etVals.length; e++) etSum += (etVals[e] || 0)
    snap.entity_counts_total = etSum
    snap.entity_counts_drift = etSum - snap.monitor_total_count
  }

  // sourceManager
  if (vm.sourceManager) {
    snap.sm_features = safeMapSize(vm.sourceManager.features)
    snap.sm_pending_pos = safeArrayLen(vm.sourceManager._pendingPositionUpdates)
    snap.sm_pending_prop = safeArrayLen(vm.sourceManager._pendingPropertyUpdates)
    snap.sm_pending_ent = safeArrayLen(vm.sourceManager._pendingEntityUpdates)
  }

  // domMarkers
  if (vm.domMarkers && vm.domMarkers.markers) {
    snap.dom_markers = safeMapSize(vm.domMarkers.markers)
  }

  // animationManager
  if (vm.animationManager) {
    snap.anim_queue = safeKeysCount(vm.animationManager._activeAnimations)
      || safeMapSize(vm.animationManager._anims)
      || 0
  }

  // _deviceEventMonitorIds (overlay de eventos device)
  snap.overlay_event_features = safeKeysCount(vm._deviceEventMonitorIds)

  // socketIOHandler subscribed cads (si está)
  if (vm.socketIOHandler && vm.socketIOHandler._subscribedCadIds) {
    snap.cad_subs = safeKeysCount(vm.socketIOHandler._subscribedCadIds)
  }

  // MapLibre image manager (texturas WebGL — incluye iconos + cluster cards)
  try {
    if (vm.map && vm.map.style && vm.map.style.imageManager
        && vm.map.style.imageManager.images) {
      snap.maplibre_images = safeKeysCount(vm.map.style.imageManager.images)
    }
  } catch (_) {}

  // cluster image cache (LRU de bitmaps de cluster cards)
  if (typeof window !== 'undefined' && window.__clusterImageCacheSize) {
    snap.cluster_img_cache = window.__clusterImageCacheSize() || 0
  }

  // Cluster cache hit/miss telemetry — delta vs snapshot anterior
  if (typeof window !== 'undefined' && window.__clusterCacheStats) {
    var s = window.__clusterCacheStats()
    if (state.prevClusterStats) {
      snap.cluster_hits_60s = (s.hits - state.prevClusterStats.hits)
        + (s.recovery_hits - state.prevClusterStats.recovery_hits)
      snap.cluster_misses_60s = s.misses - state.prevClusterStats.misses
      snap.cluster_miss_ms_60s = Math.round(s.miss_total_ms - state.prevClusterStats.miss_total_ms)
    }
    snap.cluster_hit_rate_pct = s.hit_rate_pct
    state.prevClusterStats = s
  }

  // iframes (CameraStream embebidos)
  try {
    snap.iframes = document.querySelectorAll('iframe').length
  } catch (_) {}

  // CPU proxy signals (acumulados en la ventana del minuto anterior)
  snap.long_tasks_count = state.windowLongTasks
  snap.long_tasks_ms = Math.round(state.windowLongTasksMs)
  snap.long_tasks_max_ms = Math.round(state.windowMaxLongTaskMs)
  snap.long_tasks_top = summarizeAttribution()

  snap.raf_count = state.windowRafCount
  snap.raf_jank = state.windowRafJank
  snap.raf_avg_ms = state.windowRafCount > 0
    ? Math.round((state.windowRafTotalMs / state.windowRafCount) * 10) / 10
    : 0
  snap.raf_max_ms = Math.round(state.windowMaxRafMs * 10) / 10

  var lag = summarizeEventLoopLag()
  snap.event_loop_lag_avg = lag.avg
  snap.event_loop_lag_max = lag.max
  snap.event_loop_lag_p95 = lag.p95

  // Top activity counters del minuto, ordenados desc
  var counterEntries = Object.entries(state.windowCounters)
  if (counterEntries.length > 0) {
    counterEntries.sort(function(a, b) { return b[1] - a[1] })
    var top = counterEntries.slice(0, 5).map(function(e) {
      return e[0] + '=' + e[1]
    }).join('|')
    snap.activity_top = top
  }

  // Reset window — la próxima muestra cubre el minuto siguiente
  resetWindowCounters()

  return snap
}

function rotate() {
  if (state.samples.length > state.maxSamples) {
    state.samples.splice(0, state.samples.length - state.maxSamples)
  }
}

function tick() {
  var snap = takeSnapshot()
  state.samples.push(snap)
  rotate()
  // Notice cada 30 min para confirmar que sigue vivo
  var t = nowMs()
  if (t - state.lastNotice >= state.noticeIntervals) {
    state.lastNotice = t
    var first = state.samples[0]
    var last = state.samples[state.samples.length - 1]
    console.warn('[memTracer] alive — N=' + state.samples.length
      + ' age=' + last.age_s + 's'
      + ' heap=' + last.heap_used_mb + 'MB (Δ' + (first ? Math.round((last.heap_used_mb - first.heap_used_mb) * 10) / 10 : 0) + ')'
      + ' monitors=' + last.monitors
      + ' alerts=' + last.alerts_len
      + ' images=' + last.maplibre_images)
  }
}

function start(vmOrOpts, opts) {
  // Soporta start(vm, opts) y start(opts).
  var vm = null
  if (vmOrOpts && (vmOrOpts._isVue || vmOrOpts.$options)) {
    vm = vmOrOpts
  } else if (vmOrOpts && typeof vmOrOpts === 'object') {
    opts = vmOrOpts
  }
  opts = opts || {}

  if (state.timer) {
    console.warn('[memTracer] already running — call stop() first')
    return
  }
  state.vm = vm
  state.intervalMs = opts.intervalMs || 60000
  state.maxSamples = opts.maxSamples || 240
  state.startedAt = nowMs()
  state.lastNotice = state.startedAt

  // Arrancar señales de CPU proxy ANTES del primer snapshot — el primer
  // snapshot tendrá ventana parcial pero los siguientes serán completos.
  startLongTaskObserver()
  startRafChain()
  startEventLoopLagProbe()

  // Snapshot inicial inmediato como baseline
  tick()

  state.timer = setInterval(tick, state.intervalMs)
  console.log('[memTracer] started run=' + RUN_ID
    + ' interval=' + state.intervalMs + 'ms'
    + ' maxSamples=' + state.maxSamples
    + ' (~' + Math.round(state.intervalMs * state.maxSamples / 60000) + ' min de retención)')
}

function stop() {
  if (state.timer) {
    clearInterval(state.timer)
    state.timer = null
    stopLongTaskObserver()
    stopRafChain()
    stopEventLoopLagProbe()
    console.log('[memTracer] stopped after ' + state.samples.length + ' samples')
  }
}

// API pública para que otros módulos incrementen counters de actividad
// que el snapshot consume cada minuto. Ejemplos de uso esperado:
//   memTracer.tick('source.setData')
//   memTracer.tick('source.updateData')
//   memTracer.tick('cluster.updateImages')
//   memTracer.tick('ws.message')
function tickCounter(name, n) {
  if (!name) return
  state.windowCounters[name] = (state.windowCounters[name] || 0) + (n || 1)
}

function snapshot() {
  // Manual ad-hoc snapshot — no incrementa lastNotice
  var s = takeSnapshot()
  state.samples.push(s)
  rotate()
  return s
}

function getDelta() {
  if (state.samples.length < 2) return null
  var first = state.samples[0]
  var last = state.samples[state.samples.length - 1]
  var delta = {}
  Object.keys(last).forEach(function(k) {
    if (typeof last[k] === 'number') {
      delta[k] = last[k] - (first[k] || 0)
    }
  })
  delta._first_t = first.t
  delta._last_t = last.t
  delta._span_min = Math.round((last.t - first.t) / 60000 * 10) / 10
  delta._n = state.samples.length
  return delta
}

function rowFromSample(s) {
  return {
    t_min: Math.round(s.age_s / 60 * 10) / 10,
    heap_MB: s.heap_used_mb,
    monitors: s.monitors,
    alerts: s.alerts_len,
    sm_feats: s.sm_features,
    images: s.maplibre_images,
    cluster_imgs: s.cluster_img_cache,
    // Cluster cache health: hit-rate global y deltas del minuto
    hit_rate: s.cluster_hit_rate_pct,
    misses_min: s.cluster_misses_60s,
    miss_ms_min: s.cluster_miss_ms_60s,
    dom_mark: s.dom_markers,
    anim: s.anim_queue,
    iframes: s.iframes,
    // CPU proxy — los más reveladores
    longtasks_n: s.long_tasks_count,
    longtasks_ms: s.long_tasks_ms,
    longtask_max: s.long_tasks_max_ms,
    raf_jank: s.raf_jank,
    raf_avg: s.raf_avg_ms,
    loop_lag_p95: s.event_loop_lag_p95,
    loop_lag_max: s.event_loop_lag_max,
    activity: s.activity_top,
    longtask_top: s.long_tasks_top,
  }
}

function dumpTable() {
  if (state.samples.length === 0) {
    console.log('[memTracer] no samples yet')
    return
  }
  // Reduce a max 60 filas para que la consola sea legible
  var stride = Math.max(1, Math.floor(state.samples.length / 60))
  var rows = []
  for (var i = 0; i < state.samples.length; i += stride) {
    rows.push(rowFromSample(state.samples[i]))
  }
  // Asegura el último también
  var lastSample = state.samples[state.samples.length - 1]
  if (rows.length === 0 || rows[rows.length - 1].t_min !== Math.round(lastSample.age_s / 60 * 10) / 10) {
    rows.push(rowFromSample(lastSample))
  }
  console.table(rows)
  // Tabla complementaria con leak-detection counters (drifts y queues)
  var rows2 = []
  for (var j = 0; j < state.samples.length; j += stride) {
    var s2 = state.samples[j]
    rows2.push({
      t_min: Math.round(s2.age_s / 60 * 10) / 10,
      mon_drift: s2.monitor_total_count_drift,
      alert_drift: s2.alert_id_set_drift,
      ent_drift: s2.entity_counts_drift,
      pulsable: s2.pulsable_monitors,
      pos_buf: s2.position_buffer_total,
      sm_pos_q: s2.sm_pending_pos,
      sm_prop_q: s2.sm_pending_prop,
      sm_ent_q: s2.sm_pending_ent,
      cad_subs: s2.cad_subs,
      pending_data: s2.pending_data,
      geofence: s2.geofence_data,
    })
  }
  console.log('--- drift / queue health ---')
  console.table(rows2)
}

function dumpCSV() {
  if (state.samples.length === 0) {
    console.log('[memTracer] no samples yet')
    return
  }
  var keys = Object.keys(state.samples[0])
  var out = keys.join(',') + '\n'
  for (var i = 0; i < state.samples.length; i++) {
    var s = state.samples[i]
    var line = keys.map(function(k) {
      var v = s[k]
      if (v === null || v === undefined) return ''
      if (typeof v === 'boolean') return v ? '1' : '0'
      return v
    }).join(',')
    out += line + '\n'
  }
  console.log('[memTracer] CSV (' + state.samples.length + ' samples, run=' + RUN_ID + '):\n' + out)
  return out
}

function dumpDelta() {
  var d = getDelta()
  if (!d) {
    console.log('[memTracer] not enough samples for delta')
    return
  }
  var rows = []
  Object.keys(d).forEach(function(k) {
    if (k.startsWith('_')) return
    rows.push({ metric: k, delta: d[k] })
  })
  rows.sort(function(a, b) { return Math.abs(b.delta) - Math.abs(a.delta) })
  console.log('[memTracer] delta from t=' + new Date(d._first_t).toISOString()
    + ' to t=' + new Date(d._last_t).toISOString()
    + ' (' + d._span_min + ' min, ' + d._n + ' samples)')
  console.table(rows)
}

function dump() {
  console.log('=== memTracer dump (run=' + RUN_ID + ', N=' + state.samples.length + ') ===')
  dumpTable()
  console.log('---')
  dumpDelta()
  console.log('---')
  console.log('Para CSV completo: __memTracer.dumpCSV()')
  console.log('Para export raw: __memTracer.export()')
}

function exportData() {
  return {
    runId: RUN_ID,
    startedAt: state.startedAt,
    samples: state.samples.slice(),
    delta: getDelta(),
  }
}

const memTracer = {
  start: start,
  stop: stop,
  snapshot: snapshot,
  dump: dump,
  dumpTable: dumpTable,
  dumpCSV: dumpCSV,
  dumpDelta: dumpDelta,
  getDelta: getDelta,
  export: exportData,
  tick: tickCounter,
  get samples() { return state.samples },
  get runId() { return RUN_ID },
}

export default memTracer
